import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Aviso, Botao, EstadoCarregando, EstadoErro, EstadoVazio } from '../../componentes/ui';
import { useCliente, useSpeds } from '../../hooks/useOperacao';
import { useAcessoAoCliente } from '../../hooks/useParticipacao';
import type { ClienteDetalhado } from '../../servicos/operacao';
import type { AcessoAoCliente } from '../../servicos/participacao';
import { formatarCnpj } from '../../servicos/formato';
import { AbaCadastro } from './AbaCadastro';
import { AbaColaboradores } from './AbaColaboradores';
import { AbaIcmsPassivo, useContratosPassivo } from './AbaIcmsPassivo';
import { AbaIcmsRecorrente, type ModoDaAba } from './AbaIcmsRecorrente';
import { AbaServicos } from './AbaServicos';
import { BotaoDoCrm } from '../crm/BotaoDoCrm';

/**
 * Perfil do cliente — a tela mais densa do sistema.
 *
 * ## As abas vêm do Deca 1
 *
 * Elas não são uma organização inventada: é como a operação já pensa o cliente,
 * e trocar isso custaria retreinar quem usa o sistema todo dia. O que muda é a
 * arrumação de cada uma.
 *
 * | Aba              | Responde                                        |
 * | ---------------- | ----------------------------------------------- |
 * | ICMS Recorrente  | Como está a operação de crédito deste ano       |
 * | ICMS Passivos    | Como estão as renegociações de dívida           |
 * | Cadastro         | Quem é a empresa, onde fica, para onde se paga   |
 * | Serviços         | Que produtos ela contratou — e por onde começar |
 * | Colaboradores    | Quem trabalha nela e quem assina                 |
 *
 * ## As abas de solução só existem quando há dado
 *
 * "ICMS Recorrente" aparece quando o cliente tem algum SPED ou contrato;
 * "ICMS Passivos", quando tem algum contrato de Passivo, em qualquer status —
 * uma simulação gravada já é operação começada. Um cliente recém-cadastrado
 * não tem nenhuma das duas, e abrir numa aba que só diz "nenhum resultado" é
 * abrir numa parede. Ele abre em **Serviços**, que é a porta de entrada: é lá
 * que se envia o primeiro SPED, e a aba de Recorrente nasce em seguida.
 *
 * A aba inicial é a primeira de solução que existir; sem nenhuma, Serviços.
 * Enquanto as consultas que decidem isso não respondem, a tela espera — fixar
 * uma aba antes faria o perfil piscar de Serviços para Recorrente.
 *
 * ## A aba inicial é decidida uma vez, e só muda por clique
 *
 * Ela **não** é recalculada quando chegam dados. O caso concreto: cliente novo
 * abre em Serviços, envia o primeiro SPED, o modal mostra "2 competência(s)
 * processada(s)", um arquivo recusado e os avisos de leitura. O envio invalida
 * a consulta de SPED, ela volta com linhas, e se a aba fosse derivada a cada
 * render o perfil pulava para ICMS Recorrente — desmontando Serviços junto com
 * o modal e a lista de recusados que o usuário ainda estava lendo. A aba nova
 * passa a existir na barra; a troca é dele. Enquanto isso, um aviso em Serviços
 * oferece o caminho.
 *
 * ## O que arrumei em relação ao Deca 1
 *
 * Lá, "Relatórios", "Arquivos" e "Cronograma de Envios" eram um segundo nível de
 * abas **dentro** de ICMS Recorrente, com aparência diferente do primeiro nível —
 * duas gramáticas de navegação na mesma tela. Aqui os dois níveis usam o mesmo
 * componente e a mesma marcação, então a diferença entre eles é hierarquia, não
 * estilo.
 *
 * O "Fechar Contrato" também sai de cima da lista: ele muda o estado da operação
 * inteira do ano, e ficava a um clique de distância de quem só queria trocar o
 * filtro de ano.
 *
 * ## Quem olha decide o que a tela oferece
 *
 * A mesma tela serve à operação, ao **assessor** do cliente e ao **fornecedor**
 * que vende crédito a ele (`GET /participacao/clientes/:id`):
 *
 * | Quem                     | Vê                                   | Faz                                         |
 * | ------------------------ | ------------------------------------ | ------------------------------------------- |
 * | Operação (admin)         | tudo                                 | tudo                                        |
 * | Assessor (gestor)        | Recorrente, Cadastro, Colaboradores  | cálculo, SPED, deságio, o passo dele no contrato, cadastro e colaboradores (sem nomear gestor) |
 * | O próprio cliente (gestor) | Recorrente, Cadastro, Colaboradores | SPED, lançamento, o passo dele no contrato, cadastro, conta bancária e colaboradores — deságio e valores do mês, não |
 * | Fornecedor / colaborador | Recorrente e os dados da empresa     | nada — só acompanha                          |
 *
 * Serviços (catálogo, papéis, minutas) e o Passivo do cliente são da operação.
 * A API confere cada ação de novo; isto é para não oferecer o que seria recusado.
 */

type Aba = 'icms' | 'passivo' | 'cadastro' | 'servicos' | 'colaboradores';

interface DefinicaoDeAba {
  readonly id: Aba;
  readonly rotulo: string;
  readonly icone: JSX.Element;
}

export function PerfilDoCliente(): JSX.Element {
  const { clienteId } = useParams<{ clienteId: string }>();
  const cliente = useCliente(clienteId);
  // As duas consultas que decidem as abas. A de SPED é a mesma que a aba de
  // Recorrente usa — mesma chave, uma requisição só.
  const speds = useSpeds(clienteId);
  const passivo = useContratosPassivo(clienteId);
  const acesso = useAcessoAoCliente(clienteId);

  if (acesso.erro !== null) {
    return (
      <EstadoVazio
        titulo="Este cliente não faz parte da sua operação"
        descricao="A empresa escolhida não assessora este cliente nem vende crédito a ele. Se a empresa certa é outra, troque no topo da tela."
      />
    );
  }
  if (cliente.isLoading || speds.isLoading || passivo.isLoading || acesso.carregando) {
    return <EstadoCarregando contexto="o cliente" />;
  }
  if (cliente.isError) {
    return (
      <EstadoErro
        titulo="Não foi possível carregar este cliente"
        mensagem={cliente.error.message}
        aoTentarNovamente={() => void cliente.refetch()}
      />
    );
  }

  const dados = cliente.data;
  if (dados === undefined || clienteId === undefined || acesso.acesso === undefined) {
    return <EstadoVazio titulo="Cliente não encontrado" />;
  }

  // Erro nas consultas de solução não derruba o perfil: a aba só não aparece.
  // Cadastro, Serviços e Colaboradores continuam acessíveis.
  const temRecorrente = dados.contratos.length > 0 || (speds.data?.length ?? 0) > 0;
  const temPassivo = (passivo.data?.length ?? 0) > 0;

  // A chave amarra o estado da aba ao cliente: trocar de `/clientes/a` para
  // `/clientes/b` remonta o perfil e decide a aba inicial de novo, para o
  // cliente novo — em vez de herdar a aba escolhida no anterior.
  return (
    <PerfilCarregado
      key={clienteId}
      clienteId={clienteId}
      dados={dados}
      temRecorrente={temRecorrente}
      temPassivo={temPassivo}
      acesso={acesso.acesso}
    />
  );
}

/** De onde se chega ao cliente, conforme quem olha — é para lá que a trilha volta. */
function trilhaDe(acesso: AcessoAoCliente): { readonly para: string; readonly rotulo: string } {
  switch (acesso.relacao) {
    case 'ADMIN':
      return { para: '/empresas?perfis=clientes', rotulo: 'Clientes' };
    default:
      // Assessor, fornecedor e o próprio cliente chegam pela mesma lista.
      return { para: '/icms-recorrente/negociacoes', rotulo: 'Negociações' };
  }
}

function abaInicial(temRecorrente: boolean, temPassivo: boolean): Aba {
  return temRecorrente ? 'icms' : temPassivo ? 'passivo' : 'servicos';
}

/**
 * O perfil com os dados na mão. Separado do carregamento para a aba inicial
 * poder ser um `useState` com inicializador: ele roda **uma vez**, na primeira
 * renderização com dados, e nunca mais — é o que impede a chegada de um SPED
 * de trocar a aba embaixo do usuário.
 */
function PerfilCarregado(props: {
  readonly clienteId: string;
  readonly dados: ClienteDetalhado;
  readonly temRecorrente: boolean;
  readonly temPassivo: boolean;
  readonly acesso: AcessoAoCliente;
}): JSX.Element {
  const { clienteId, dados, temPassivo, acesso } = props;
  const admin = acesso.relacao === 'ADMIN';
  // Fora da operação, a porta de entrada é o Recorrente — com ou sem cálculo
  // ainda: é lá que o assessor envia o primeiro SPED (Serviços é da operação).
  const temRecorrente = props.temRecorrente || !admin;
  const modo: ModoDaAba = admin
    ? 'operacao'
    : !acesso.podeGerenciar
      ? 'leitura'
      : acesso.relacao === 'CLIENTE'
        ? 'cliente'
        : 'gestao';
  const [inicial] = useState<Aba>(() => abaInicial(temRecorrente, temPassivo));
  const [abaEscolhida, setAba] = useState<Aba>(inicial);

  const abas: readonly DefinicaoDeAba[] = [
    ...(temRecorrente
      ? [{ id: 'icms' as const, rotulo: 'ICMS Recorrente', icone: <IconeIcms /> }]
      : []),
    ...(temPassivo
      ? [{ id: 'passivo' as const, rotulo: 'ICMS Passivos', icone: <IconePassivo /> }]
      : []),
    { id: 'cadastro', rotulo: 'Cadastro', icone: <IconeCadastro /> },
    ...(admin ? [{ id: 'servicos' as const, rotulo: 'Serviços', icone: <IconeServicos /> }] : []),
    ...(admin || acesso.podeGerenciar
      ? [{ id: 'colaboradores' as const, rotulo: 'Colaboradores', icone: <IconeColaboradores /> }]
      : []),
  ];

  // A aba escolhida pode ter deixado de existir (o último contrato de Passivo
  // sumiu enquanto ela estava aberta); só nesse caso a tela decide sozinha, e
  // decide pelo que existe agora.
  const aba: Aba = abas.some((a) => a.id === abaEscolhida)
    ? abaEscolhida
    : abaInicial(temRecorrente, temPassivo);
  const trilha = trilhaDe(acesso);

  // O cliente abriu sem operação e ganhou uma enquanto a tela estava em
  // Serviços: a aba nasceu na barra, e este aviso é o atalho até ela.
  const recorrenteNasceu = aba === 'servicos' && inicial === 'servicos' && temRecorrente;

  return (
    <div className="flex w-full flex-col">
      {/* Faixa de identificação, fixa em todas as abas. */}
      <header className="-mx-4 -mt-8 border-b border-borda bg-superficie-cabecalho px-4 pb-0 pt-6 sm:-mx-8 sm:px-8">
        <nav aria-label="Trilha" className="text-sm text-conteudo-secundario">
          <Link
            to={trilha.para}
            className="underline-offset-4 hover:text-conteudo-primario hover:underline"
          >
            {trilha.rotulo}
          </Link>
          <span className="mx-2 text-conteudo-sutil">/</span>
          <span>Perfil</span>
        </nav>

        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <h1 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
            {dados.razaoSocial}
          </h1>
          {/* O perfil não sabe se a empresa está ativa; se não estiver, o servidor
            recusa o vínculo e o botão mostra o motivo. */}
          <BotaoDoCrm businessId={clienteId} ativa />
        </div>
        <p className="valor-fiscal mt-1 text-sm text-conteudo-secundario">
          {formatarCnpj(dados.cnpj)}
        </p>
        {/* RN-621 — de quem é este cliente. Trocar é na aba Serviços (admin). */}
        <p className="mt-2 text-sm text-conteudo-secundario">
          Assessor:{' '}
          <span className="text-conteudo-primario">
            {dados.assessorResponsavel?.razaoSocial ?? 'operadora (sem assessor definido)'}
          </span>
        </p>

        {/* As abas encostam na borda inferior da faixa, como no Deca 1. */}
        <div role="group" aria-label="Seções do cliente" className="mt-6 flex flex-wrap gap-1">
          {abas.map((a) => {
            const ativa = aba === a.id;
            return (
              <button
                key={a.id}
                type="button"
                aria-pressed={ativa}
                onClick={() => setAba(a.id)}
                className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano ${
                  ativa
                    ? 'border-acento-ciano font-medium text-acento-ciano'
                    : 'border-transparent text-conteudo-secundario hover:text-conteudo-primario'
                }`}
              >
                {a.icone}
                {a.rotulo}
              </button>
            );
          })}
        </div>
      </header>

      <div className="pt-8">
        {aba === 'icms' && (
          <AbaIcmsRecorrente clienteId={clienteId} contratos={dados.contratos} modo={modo} />
        )}
        {aba === 'passivo' && <AbaIcmsPassivo clienteId={clienteId} />}
        {aba === 'cadastro' && (
          <AbaCadastro
            businessId={clienteId}
            rotaAposDesativar="/empresas?perfis=clientes"
            {...(admin ? {} : { modo: acesso.podeGerenciar ? 'gestao' : 'leitura' })}
          />
        )}
        {aba === 'servicos' && (
          <div className="flex flex-col gap-6">
            {recorrenteNasceu && (
              <Aviso
                tom="positivo"
                titulo="A aba ICMS Recorrente passou a existir"
                acao={
                  <Botao variante="secundario" tamanho="pequeno" onClick={() => setAba('icms')}>
                    Ver em ICMS Recorrente
                  </Botao>
                }
              >
                O cliente já tem cálculo. Os arquivos, as competências e o contrato ficam lá; esta
                aba continua sendo o lugar de enviar novos SPEDs.
              </Aviso>
            )}
            <AbaServicos
              businessId={clienteId}
              {...(temRecorrente ? {} : { semOperacaoRecorrente: true })}
            />
          </div>
        )}
        {aba === 'colaboradores' && (
          <AbaColaboradores
            businessId={clienteId}
            razaoSocial={dados.razaoSocial}
            podeDefinirPapeis={acesso.podeGerenciarDiretamente}
          />
        )}
      </div>
    </div>
  );
}

/* ─── Ícones das abas ─────────────────────────────────────────────────────── */

const traco = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function IconeIcms(): JSX.Element {
  return (
    <svg {...traco}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
    </svg>
  );
}

function IconePassivo(): JSX.Element {
  return (
    <svg {...traco}>
      <path d="M12 3v18" />
      <path d="M17 6.5A3.5 3.5 0 0 0 13.5 4h-2a3.5 3.5 0 0 0 0 7h1a3.5 3.5 0 0 1 0 7h-2A3.5 3.5 0 0 1 7 17.5" />
    </svg>
  );
}

function IconeCadastro(): JSX.Element {
  return (
    <svg {...traco}>
      <path d="M4 21h16" />
      <path d="M5 21V9l7-5 7 5v12" />
      <path d="M10 21v-6h4v6" />
    </svg>
  );
}

function IconeServicos(): JSX.Element {
  return (
    <svg {...traco}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v10M9.5 9.5h4a1.75 1.75 0 0 1 0 3.5h-3a1.75 1.75 0 0 0 0 3.5h4" />
    </svg>
  );
}

function IconeColaboradores(): JSX.Element {
  return (
    <svg {...traco}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 11a3 3 0 0 0 0-6" />
      <path d="M18.5 20a5 5 0 0 0-2.2-4.1" />
    </svg>
  );
}
